import React from 'react';
import { useIntl } from 'react-intl';
import { FuelTransactionFlag } from '../../redux/store/userData/fuelTransactions/initialState';

type Props = {
  flags?: FuelTransactionFlag[] | null;
};

const RULE_LABELS: Record<string, string> = {
  tankOverfill: 'Tank overfill',
  productMismatch: 'Product mismatch',
  outsideTrip: 'Outside trip',
  repeatFuelling: 'Repeat fuelling',
  farFromStation: 'Far from station',
  priceOutlier: 'Price outlier',
  priceMismatch: 'Price mismatch',
  levelNotRising: 'Fuel level not rising',
};

// Backend reason texts use {{name}} placeholders, which ICU formatMessage cannot parse.
const PLACEHOLDER = /\{\{(\w+)\}\}/g;

const FuelTransactionFlagReasons: React.FC<Props> = ({ flags }) => {
  const intl = useIntl();

  const localizedReason = ({
    reason,
    reasonKey,
    reasonParams,
  }: FuelTransactionFlag): string | null => {
    const template = reasonKey ? intl.messages[reasonKey] : undefined;
    if (typeof template !== 'string') return reason || null;
    const filled = template.replace(
      PLACEHOLDER,
      (match, name: string) => reasonParams?.[name] ?? match
    );
    return filled.includes('{{') ? reason || null : filled;
  };

  const entries = (flags ?? [])
    .map((flag) => ({ rule: flag.rule, reason: localizedReason(flag) }))
    .filter(
      (entry): entry is { rule: string | null | undefined; reason: string } =>
        !!entry.reason
    );

  if (entries.length === 0) return <span>-</span>;

  const ruleLabel = (rule?: string | null) => {
    if (!rule) return null;
    return intl.formatMessage({
      id: `fleetadmin.fuelTransactions.flagRule.${rule}`,
      defaultMessage: RULE_LABELS[rule] ?? rule,
    });
  };

  return (
    <div data-testid="FuelTransactionFlagReasons">
      {entries.map(({ rule, reason }, index) => {
        const label = ruleLabel(rule);
        return (
          <div key={index} style={{ whiteSpace: 'normal', marginBottom: 4 }}>
            {'- '}
            {label ? (
              <>
                <strong>{label}</strong> =&gt; {reason}
              </>
            ) : (
              reason
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FuelTransactionFlagReasons;
